import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Building2,
  LayoutDashboard,
  Building,
  Home,
  FileText as LeaseIcon,
  DollarSign,
  Wrench,
  FileText,
  Users,
  Calculator,
  Clock,
  PiggyBank,
  Scale,
  Truck,
  MessageSquare,
  CreditCard,
  BarChart3,
  UserCog,
  History,
  Upload,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Bell,
  Check,
  Megaphone,
  Gavel
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/*
 * Altam™ Operating Services
 * Main Layout with Role-Based Navigation
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

// Navigation configuration for different roles
const getNavigationForRole = (role) => {
  // Resident Portal - Limited access
  if (role === 'resident') {
    return [
      {
        title: 'Dashboard',
        items: [
          { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }
        ]
      },
      {
        title: 'Services',
        items: [
          { name: 'Maintenance Requests', href: '/maintenance', icon: Wrench },
          { name: 'Message Center', href: '/communications', icon: MessageSquare }
        ]
      }
    ];
  }

  // Employee Portal - Time tracking, maintenance, payroll, communications
  if (role === 'employee') {
    return [
      {
        title: 'Dashboard',
        items: [
          { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }
        ]
      },
      {
        title: 'My Work',
        items: [
          { name: 'Time Entries', href: '/time-entries', icon: Clock },
          { name: 'Work Orders', href: '/maintenance', icon: Wrench },
          { name: 'Message Center', href: '/communications', icon: MessageSquare }
        ]
      },
      {
        title: 'Pay & Benefits',
        items: [
          { name: 'My Pay Stubs', href: '/payroll', icon: Calculator }
        ]
      }
    ];
  }

  // Board Portal - Read-only access to board reports with download
  if (role === 'board') {
    return [
      {
        title: 'Dashboard',
        items: [
          { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }
        ]
      },
      {
        title: 'Board Reports',
        items: [
          { name: 'Board Reports', href: '/reports', icon: BarChart3 },
          { name: 'Financial Summary', href: '/reports/financial', icon: DollarSign },
          { name: 'Property Overview', href: '/reports/properties', icon: Building }
        ]
      },
      {
        title: 'Governance',
        items: [
          { name: 'Meeting Minutes', href: '/meeting-minutes', icon: Gavel },
          { name: 'Bylaws & Policies', href: '/bylaws', icon: FileText },
          { name: 'AGM Documents', href: '/agm-documents', icon: Users }
        ]
      }
    ];
  }

  // Manager Portal - Same as admin except user management and audit logs
  if (role === 'manager') {
    return [
      {
        title: 'Dashboard',
        items: [
          { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }
        ]
      },
      {
        title: 'Property Management',
        items: [
          { name: 'Properties', href: '/properties', icon: Building },
          { name: 'Units', href: '/units', icon: Home },
          { name: 'Leases', href: '/leases', icon: LeaseIcon }
        ]
      },
      {
        title: 'Rent Collection',
        items: [
          { name: 'Rent Payments', href: '/collections/rent', icon: DollarSign },
          { name: 'Online Payments', href: '/online-payments', icon: CreditCard },
          { name: 'Transactions', href: '/transactions', icon: CreditCard }
        ]
      },
      {
        title: 'Condo Management',
        items: [
          { name: 'Maintenance Fees', href: '/collections/maintenance', icon: Home }
        ]
      },
      {
        title: 'Invoicing',
        items: [
          { name: 'All Invoices', href: '/invoices', icon: FileText },
          { name: 'Management Fees', href: '/invoices/management', icon: Building }
        ]
      },
      {
        title: 'Banking',
        items: [
          { name: 'Bank Accounts', href: '/bank-accounts', icon: PiggyBank },
          { name: 'Reconciliation', href: '/reconciliation', icon: Scale },
          { name: 'Cheques & Deposits', href: '/cheques', icon: FileText }
        ]
      },
      {
        title: 'Operations',
        items: [
          { name: 'Maintenance', href: '/maintenance', icon: Wrench },
          { name: 'Suppliers', href: '/suppliers', icon: Truck },
          { name: 'Communications', href: '/communications', icon: MessageSquare }
        ]
      },
      {
        title: 'Payroll & HR',
        items: [
          { name: 'Employees', href: '/employees', icon: Users },
          { name: 'Payroll', href: '/payroll', icon: Calculator },
          { name: 'Time Entries', href: '/time-entries', icon: Clock }
        ]
      },
      {
        title: 'Governance',
        items: [
          { name: 'Meeting Minutes', href: '/meeting-minutes', icon: Gavel },
          { name: 'Bylaws & Policies', href: '/bylaws', icon: FileText },
          { name: 'AGM Documents', href: '/agm-documents', icon: Users }
        ]
      },
      {
        title: 'Reports',
        items: [
          { name: 'Reports', href: '/reports', icon: BarChart3 },
          { name: 'Import Data', href: '/import', icon: Upload }
        ]
      }
    ];
  }

  // Admin Portal - Full access
  return [
    {
      title: 'Dashboard',
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }
      ]
    },
    {
      title: 'Property Management',
      items: [
        { name: 'Properties', href: '/properties', icon: Building },
        { name: 'Units', href: '/units', icon: Home },
        { name: 'Leases', href: '/leases', icon: LeaseIcon }
      ]
    },
    {
      title: 'Rent Collection',
      items: [
        { name: 'Rent Payments', href: '/collections/rent', icon: DollarSign },
        { name: 'Online Payments', href: '/online-payments', icon: CreditCard },
        { name: 'Transactions', href: '/transactions', icon: CreditCard }
      ]
    },
    {
      title: 'Condo Management',
      items: [
        { name: 'Maintenance Fees', href: '/collections/maintenance', icon: Home }
      ]
    },
    {
      title: 'Invoicing',
      items: [
        { name: 'All Invoices', href: '/invoices', icon: FileText },
        { name: 'Management Fees', href: '/invoices/management', icon: Building }
      ]
    },
    {
      title: 'Banking',
      items: [
        { name: 'Bank Accounts', href: '/bank-accounts', icon: PiggyBank },
        { name: 'Reconciliation', href: '/reconciliation', icon: Scale },
        { name: 'Cheques & Deposits', href: '/cheques', icon: FileText }
      ]
    },
    {
      title: 'Operations',
      items: [
        { name: 'Maintenance', href: '/maintenance', icon: Wrench },
        { name: 'Suppliers', href: '/suppliers', icon: Truck },
        { name: 'Communications', href: '/communications', icon: MessageSquare }
      ]
    },
    {
      title: 'Payroll & HR',
      items: [
        { name: 'Employees', href: '/employees', icon: Users },
        { name: 'Payroll', href: '/payroll', icon: Calculator },
        { name: 'Time Entries', href: '/time-entries', icon: Clock }
      ]
    },
    {
      title: 'Governance',
      items: [
        { name: 'Meeting Minutes', href: '/meeting-minutes', icon: Gavel },
          { name: 'Bylaws & Policies', href: '/bylaws', icon: FileText },
          { name: 'AGM Documents', href: '/agm-documents', icon: Users }
      ]
    },
    {
      title: 'Admin',
      items: [
        { name: 'Reports', href: '/reports', icon: BarChart3 },
        { name: 'User Management', href: '/users', icon: UserCog },
        { name: 'Audit Log', href: '/audit-log', icon: History },
        { name: 'Import Data', href: '/import', icon: Upload }
      ]
    }
  ];
};

// Portal labels based on role
const getPortalLabel = (role) => {
  switch (role) {
    case 'resident': return 'Resident Portal';
    case 'employee': return 'Employee Portal';
    case 'manager': return 'Manager Portal';
    case 'board': return 'Board Portal';
    default: return 'Admin Portal';
  }
};

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState(['Dashboard', 'Services', 'My Work', 'Property Management', 'Finance', 'Operations']);
  const { user, logout, showTimeoutWarning, timeoutCountdown, extendSession, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [token]);

  // Fetch notifications on mount and every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
    setNotificationsOpen(false);
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleGroup = (title) => {
    setExpandedGroups(prev => 
      prev.includes(title) 
        ? prev.filter(g => g !== title)
        : [...prev, title]
    );
  };

  // Get navigation based on user role
  const navigationGroups = getNavigationForRole(user?.role);
  const portalLabel = getPortalLabel(user?.role);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Session Timeout Warning Modal */}
      {showTimeoutWarning && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="timeout-warning-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Session Timeout Warning</h3>
                <p className="text-sm text-slate-500">Your session is about to expire</p>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 text-center">
                You will be logged out in <span className="font-bold text-2xl">{formatCountdown(timeoutCountdown)}</span>
              </p>
              <p className="text-amber-700 text-sm text-center mt-1">due to inactivity</p>
            </div>
            
            <p className="text-slate-600 text-sm mb-4">
              Click "Stay Logged In" to continue your session, or "Logout" to end your session now.
            </p>
            
            <div className="flex gap-3">
              <button 
                onClick={handleLogout}
                className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium transition-colors"
              >
                Logout
              </button>
              <button 
                onClick={extendSession}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-violet-700 transition-colors"
                data-testid="extend-session-btn"
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900 text-lg">ALTAM™</h1>
              <p className="text-xs text-slate-500">{portalLabel}</p>
            </div>
          </Link>
          <button 
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 overflow-y-auto h-[calc(100vh-8rem)]">
          {navigationGroups.map((group) => {
            const isExpanded = expandedGroups.includes(group.title);
            const hasActiveItem = group.items.some(item => location.pathname === item.href);
            
            return (
              <div key={group.title} className="mb-2">
                {group.title !== 'Dashboard' && (
                  <button
                    onClick={() => toggleGroup(group.title)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg transition-colors ${
                      hasActiveItem ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {group.title}
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}
                {(isExpanded || group.title === 'Dashboard') && (
                  <div className="mt-1 space-y-1">
                    {group.items.map((item) => {
                      const isActive = location.pathname === item.href;
                      const isComingSoon = item.comingSoon;
                      
                      if (isComingSoon) {
                        return (
                          <div
                            key={item.name}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 cursor-not-allowed"
                            title="Coming Soon"
                          >
                            <item.icon className="w-5 h-5 text-slate-300" />
                            <span className="flex-1">{item.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-full">Soon</span>
                          </div>
                        );
                      }
                      
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-gradient-to-r from-indigo-50 to-violet-50 text-indigo-700 border border-indigo-100'
                              : 'text-slate-600 hover:bg-slate-100'
                          }`}
                          data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <item.icon className={`w-5 h-5 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-full flex items-center justify-center text-white font-semibold">
              {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user?.full_name || user?.email}
              </p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6">
          <button 
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5 text-slate-500" />
          </button>

          <div className="flex-1 lg:flex-none" />

          <div className="flex items-center gap-3">
            {/* Demo badge */}
            <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Demo Instance
            </span>

            {/* Portal badge */}
            <span className={`hidden md:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              user?.role === 'resident' ? 'bg-emerald-100 text-emerald-800' :
              user?.role === 'employee' ? 'bg-blue-100 text-blue-800' :
              user?.role === 'manager' ? 'bg-violet-100 text-violet-800' :
              'bg-indigo-100 text-indigo-800'
            }`}>
              {portalLabel}
            </span>

            {/* Notifications */}
            <div className="relative">
              <button 
                className="p-2 hover:bg-slate-100 rounded-lg relative"
                onClick={() => {
                  setNotificationsOpen(!notificationsOpen);
                  setUserMenuOpen(false);
                }}
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5 text-slate-500" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] text-white font-bold flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notifications-dropdown">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button 
                        onClick={markAllAsRead}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                      >
                        <Check className="w-3 h-3" />
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="py-8 text-center">
                        <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notification) => (
                        <button
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors ${
                            !notification.read ? 'bg-indigo-50/50' : ''
                          }`}
                          data-testid={`notification-${notification.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              notification.type === 'announcement' 
                                ? 'bg-violet-100 text-violet-600' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              <Megaphone className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-medium truncate ${!notification.read ? 'text-slate-900' : 'text-slate-600'}`}>
                                  {notification.title}
                                </p>
                                {!notification.read && (
                                  <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-xs text-slate-500 truncate mt-0.5">{notification.message}</p>
                              <p className="text-[10px] text-slate-400 mt-1">{formatTimeAgo(notification.created_at)}</p>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
                      <Link 
                        to="/communications" 
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        onClick={() => setNotificationsOpen(false)}
                      >
                        View all communications →
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User menu */}
            <div className="relative">
              <button 
                className="flex items-center gap-2 p-2 hover:bg-slate-100 rounded-lg"
                onClick={() => {
                  setUserMenuOpen(!userMenuOpen);
                  setNotificationsOpen(false);
                }}
              >
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <Link 
                    to="/settings" 
                    className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                  <button 
                    className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>

        {/* Footer */}
        <footer className="p-4 text-center text-xs text-slate-400 border-t border-slate-200">
          <div>ALTAM™ Operating Services • © 2026 Pecaro Consulting Inc. • Property, Finance, and Governance — Unified</div>
          <div className="mt-1 text-slate-300">ALTAM™ is a registered trademark of Pecaro Consulting Inc.</div>
        </footer>
      </div>
    </div>
  );
}
